<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatUsdFromCents } from '../helpers/money';
import { formatBillingDateTime } from '../helpers/formatBillingTime';

const props = defineProps({
  wallet: { type: Object, default: null },
  ledger: { type: Array, default: () => [] },
  ledgerLoading: { type: Boolean, default: false },
  ledgerError: { type: String, default: '' },
  showLedger: { type: Boolean, default: false },
  topupSubmitting: { type: Boolean, default: false },
  topupError: { type: String, default: '' },
  topupNotice: { type: String, default: '' },
});

const emit = defineEmits(['toggle-ledger', 'topup']);

const { t } = useI18n();

const selectedTier = ref(null);

const balanceLabel = computed(() =>
  formatUsdFromCents(props.wallet?.balance_cents)
);

const pendingLabel = computed(() =>
  formatUsdFromCents(props.wallet?.pending_collection_cents || 0)
);

const tiers = computed(() => {
  const list = props.wallet?.topup_tiers_cents;
  return Array.isArray(list) && list.length
    ? list
    : [2000, 5000, 10000, 20000];
});

const topupEnabled = computed(() => props.wallet?.topup_enabled !== false);

const spendFrozen = computed(() => props.wallet?.wallet_spend_frozen === true);

const pendingCollection = computed(
  () => Number(props.wallet?.pending_collection_cents || 0) > 0
);

const entryTypeLabel = type => {
  const key = `CUSTOM.BILLING.WALLET.ENTRY_${String(type || '').toUpperCase()}`;
  const translated = t(key);
  return translated !== key ? translated : type;
};

const onTopup = () => {
  if (!selectedTier.value || props.topupSubmitting || !topupEnabled.value) return;
  emit('topup', selectedTier.value);
};
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.WALLET.TITLE')"
    :description="t('CUSTOM.BILLING.WALLET.DESCRIPTION')"
  >
    <template #action>
      <Button
        size="sm"
        color="slate"
        variant="outline"
        :label="
          showLedger
            ? t('CUSTOM.BILLING.WALLET.HIDE_LEDGER')
            : t('CUSTOM.BILLING.WALLET.SHOW_LEDGER')
        "
        @click="emit('toggle-ledger')"
      />
    </template>

    <div class="rounded-lg bg-n-alpha-1 p-4">
      <div class="text-xs text-n-slate-11">
        {{ t('CUSTOM.BILLING.WALLET.BALANCE') }}
      </div>
      <div class="mt-1 text-2xl font-semibold text-n-slate-12 tracking-tight">
        {{ balanceLabel }}
      </div>

      <div
        v-if="pendingCollection || spendFrozen"
        class="mt-3 rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-xs text-n-slate-11"
      >
        <div v-if="pendingCollection">
          {{ t('CUSTOM.BILLING.WALLET.PENDING_COLLECTION') }}:
          <span class="font-medium text-n-slate-12">{{ pendingLabel }}</span>
        </div>
        <div v-if="spendFrozen" class="mt-1">
          {{ t('CUSTOM.BILLING.WALLET.SPEND_FROZEN') }}
        </div>
      </div>

      <div class="mt-4">
        <div class="text-xs font-medium text-n-slate-12">
          {{ t('CUSTOM.BILLING.WALLET.TOPUP_TITLE') }}
        </div>
        <p class="mb-3 mt-1 text-xs text-n-slate-11">
          {{
            topupEnabled
              ? t('CUSTOM.BILLING.WALLET.TOPUP_HINT')
              : t('CUSTOM.BILLING.WALLET.TOPUP_UNAVAILABLE')
          }}
        </p>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="tier in tiers"
            :key="tier"
            type="button"
            class="min-w-[4.5rem] rounded-md border px-3 py-2 text-sm tabular-nums transition-colors"
            :class="
              selectedTier === tier
                ? 'border-n-slate-12 bg-n-slate-12 text-n-slate-1'
                : 'border-n-weak bg-n-solid-1 text-n-slate-12 hover:border-n-slate-11'
            "
            :disabled="!topupEnabled || topupSubmitting"
            @click="selectedTier = tier"
          >
            {{ formatUsdFromCents(tier) }}
          </button>
        </div>

        <div class="mt-3 flex items-center gap-3">
          <Button
            size="sm"
            color="blue"
            :label="t('CUSTOM.BILLING.WALLET.TOPUP_CTA')"
            :is-loading="topupSubmitting"
            :disabled="!topupEnabled || !selectedTier || topupSubmitting"
            @click="onTopup"
          />
        </div>

        <p
          v-if="topupNotice"
          class="mb-0 mt-2 text-xs text-n-teal-11"
          role="status"
        >
          {{ topupNotice }}
        </p>
        <p
          v-if="topupError"
          class="mb-0 mt-2 text-xs text-n-ruby-9"
          role="alert"
        >
          {{ topupError }}
        </p>
      </div>
    </div>

    <div v-if="showLedger" class="mt-4 space-y-2">
      <div v-if="ledgerLoading" class="text-sm text-n-slate-11">
        {{ t('CUSTOM.COMMON.LOADING') }}
      </div>
      <div v-else-if="ledgerError" class="text-sm text-n-ruby-9" role="alert">
        {{ t('CUSTOM.BILLING.ERRORS.LEDGER') }}: {{ ledgerError }}
      </div>
      <div v-else-if="!ledger.length" class="text-sm text-n-slate-11">
        {{ t('CUSTOM.BILLING.WALLET.LEDGER_EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full text-sm text-start">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 font-medium">
                {{ t('CUSTOM.BILLING.WALLET.COL_TIME') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CUSTOM.BILLING.WALLET.COL_TYPE') }}
              </th>
              <th class="px-3 py-2 font-medium text-end">
                {{ t('CUSTOM.BILLING.WALLET.COL_AMOUNT') }}
              </th>
              <th class="px-3 py-2 font-medium text-end">
                {{ t('CUSTOM.BILLING.WALLET.COL_BALANCE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CUSTOM.BILLING.WALLET.COL_NOTE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in ledger"
              :key="entry.id"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="px-3 py-2 whitespace-nowrap">
                {{ formatBillingDateTime(entry.created_at) }}
              </td>
              <td class="px-3 py-2">{{ entryTypeLabel(entry.entry_type) }}</td>
              <td class="px-3 py-2 text-end tabular-nums">
                {{ formatUsdFromCents(entry.amount_cents) }}
              </td>
              <td class="px-3 py-2 text-end tabular-nums">
                {{ formatUsdFromCents(entry.balance_after_cents) }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ entry.merchant_note || entry.order_number || '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </BillingSectionCard>
</template>
